import { Component, OnInit, forwardRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
// Migrated 1:1 from @nexato/nx-core-module (label/components/label-widget/label-widget-input.component.ts, #683)
import { Label } from '../../../entities/label/label';
import { LabelInput } from '../../../entities/label/labelInput';
import { EditSelectable } from '../../edit-select-form-control/edit-select-form-control.component';
import { LabelService } from '../../../services/label/label.service';

@Component({
  selector: 'nx-label-widget-input',
  templateUrl: './label-widget-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LabelWidgetInputComponent),
      multi: true
    }
  ]
})
export class LabelWidgetInputComponent implements OnInit, ControlValueAccessor {

  public placeholder = "Labels auswählen";
  public isLoading: boolean = false;
  public allLabels: Label[] = [];
  public options: EditSelectable[] = [];

  selectedControl = new FormControl<Label[] | null>(null);
  form = new FormGroup({
    selected: this.selectedControl
  });

  // Injected services
  private readonly labelService = inject(LabelService);
  private readonly destroyRef = inject(DestroyRef);

  private onChange: (value: LabelInput[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.loadAvailableLabels();

    // Subscribe to form changes and emit LabelInput array
    this.selectedControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(selectedLabels => {
        const labelInputs = this.convertToLabelInputs(selectedLabels);
        this.onChange(labelInputs);
        this.onTouched();
      });
  }

  /**
   * Load available labels from backend
   */
  private loadAvailableLabels(): void {
    this.isLoading = true;

    this.labelService.getLabels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (labels) => {
          this.allLabels = labels;
          this.options = this.allLabels;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading labels:', error);
          this.isLoading = false;
        }
      });
  }

  /**
   * Converts selected Labels to LabelInput array
   */
  private convertToLabelInputs(labels: Label[] | null): LabelInput[] {
    if (!labels || labels.length === 0) {
      return [];
    }

    return labels.map(label => {
      const labelInput = new LabelInput();
      labelInput.id = label.id;
      labelInput.externalId = label.externalId;
      labelInput.name = label.name;
      labelInput.scope = label.scope;
      labelInput.scopedName = label.scopedName;
      labelInput.color = label.color;
      labelInput.description = label.description;
      return labelInput;
    });
  }

  // ControlValueAccessor implementation

  writeValue(value: LabelInput[] | null): void {
    if (value && value.length > 0) {
      // Convert LabelInputs back to Labels for display
      const labels = value.map(input => new Label(input));
      this.selectedControl.setValue(labels, { emitEvent: false });
    } else {
      this.selectedControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: LabelInput[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedControl.disable();
    } else {
      this.selectedControl.enable();
    }
  }
}
